import React, { Component, ReactNode } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string;
  retrying: boolean;
  retryCount: number;
}

class ClerkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorMessage: '',
      retrying: false,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Check if it's a Clerk loading error
    if (error.message?.includes('Clerk: Failed to load')) {
      return {
        hasError: true,
        errorMessage: 'Unable to load authentication service. This might be due to network issues.'
      };
    }
    return {
      hasError: true,
      errorMessage: error.message
    };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('Clerk Error Boundary caught:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ 
      retrying: true, 
      retryCount: this.state.retryCount + 1 
    });
    
    // Force reload after a short delay
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh',
            p: 3
          }}
        >
          <Typography variant="h5" gutterBottom>
            Connection Error
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center', maxWidth: 600 }}>
            {this.state.errorMessage}
          </Typography>
          
          {this.state.retrying ? (
            <CircularProgress />
          ) : (
            <>
              <Button 
                variant="contained" 
                onClick={this.handleRetry}
                sx={{ mb: 2 }}
              >
                Retry Connection
              </Button>
              
              {this.state.retryCount > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Retry attempts: {this.state.retryCount}
                </Typography>
              )}
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
                If this persists, please check your internet connection or try again later.
              </Typography>
            </>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ClerkErrorBoundary;